/**
 * CSS selectors and IDs used by the screenshot/unfurl service.
 * These are shared between the frontend (where elements are rendered) and backend (where Playwright waits for them).
 *
 * IMPORTANT: Changes to these constants must be coordinated between frontend and backend deployments.
 */
/**
 * ID of the element that signals when a dashboard/chart is ready for screenshot.
 * The UnfurlService waits for this element to appear before taking a screenshot.
 *
 * Usage:
 * - Frontend: Rendered by ScreenshotReadyIndicator component
 * - Backend: Waited for by UnfurlService.saveScreenshot()
 */
export const SCREENSHOT_READY_INDICATOR_ID = 'lightdash-ready-indicator';
/**
 * ID of an element that is always mounted on minimal pages and exposes the
 * current screenshot progress: which tiles are expected, ready, and errored.
 *
 * Unlike SCREENSHOT_READY_INDICATOR_ID, this element exists from first paint
 * so the UnfurlService can read it on timeout to identify which tile UUIDs
 * are still unaccounted for.
 *
 * Data attributes:
 * - data-tiles-expected: JSON array of tile UUIDs expected to render
 * - data-tiles-ready:    JSON array of tile UUIDs that called markTileScreenshotReady
 * - data-tiles-errored:  JSON array of tile UUIDs that called markTileScreenshotErrored
 *
 * Usage:
 * - Frontend: Rendered by ScreenshotProgressIndicator on minimal pages
 * - Backend:  Read by UnfurlService when waitForSelector(READY_INDICATOR) times out
 */
export const SCREENSHOT_PROGRESS_INDICATOR_ID = 'lightdash-screenshot-progress';
/**
 * Class name for tile loading skeleton overlays.
 * The UnfurlService waits for these elements to be hidden (loading complete).
 *
 * Usage:
 * - Frontend: Applied by TileBaseV2 LoadingSkeletonOverlay
 * - Backend: Waited for by UnfurlService.saveScreenshot()
 */
export const LOADING_CHART_OVERLAY_CLASS = 'loading_chart_overlay';
/**
 * Class name for chart loading state.
 * The UnfurlService waits for these elements to be hidden (loading complete).
 *
 * Usage:
 * - Frontend: Applied by chart components during loading
 * - Backend: Waited for by UnfurlService.saveScreenshot()
 */
export const LOADING_CHART_CLASS = 'loading_chart';
/**
 * Class name for markdown tile content.
 * The UnfurlService waits for these elements to be attached to the DOM.
 *
 * Usage:
 * - Frontend: Applied to MarkdownPreview in DashboardMarkdownTile
 * - Backend: Waited for by UnfurlService.saveScreenshot()
 */
export const MARKDOWN_TILE_CLASS = 'markdown-tile';
/**
 * Attribute set on a custom chart type (`DATA_APP_VIZ`) tile that reported
 * screenshot-ready through the fallback timer because its bundle never
 * acknowledged the render (`viz-rendered`). The value is
 * `<dataAppVizUuid>@<version>`.
 *
 * Usage:
 * - Frontend: Set by DataAppVizRenderer when the render-ack fallback fires
 * - Backend: Counted by UnfurlService.saveScreenshot() after the ready wait
 */
export const CUSTOM_CHART_READY_FALLBACK_ATTRIBUTE = 'data-custom-chart-ready-fallback';
/**
 * Class name for the dashboard screenshot/PDF target wrapper.
 * Wraps the dashboard's grid(s) so the backend can measure and screenshot a
 * single deterministic element. When a multi-tab PDF export is rendered we
 * stack one <ResponsiveGridLayout> per tab; this wrapper encompasses all of
 * them so getBoundingClientRect / locator.screenshot capture every tab.
 *
 * Note: do NOT use `react-grid-layout` here — that class is also applied by
 * the third-party grid library to every grid instance, so a multi-grid render
 * would yield multiple matches and the backend would only see the first one.
 *
 * Usage:
 * - Frontend: Applied to the wrapper <div> in MinimalDashboardContent
 * - Backend: Screenshot selector in UnfurlService.saveScreenshot()
 */
export const DASHBOARD_GRID_CLASS = 'lightdash-dashboard-screenshot-target';
/**
 * Class name for a single tab's page container in the css-paged PDF export.
 * The frontend wraps each exported tab in this class; the backend measures
 * these containers to derive a uniform PDF page height and the print CSS
 * forces a page break before each one.
 *
 * Usage:
 * - Frontend: Applied per tab group in MinimalDashboardContent (paged export)
 * - Backend: Measured by UnfurlService.unfurlPdfCssPaged / generatePdf
 */
export const EXPORT_TAB_PAGE_CLASS = 'lightdash-export-tab-page';
/**
 * ID of the element rendered by the error boundary fallback.
 * The UnfurlService checks for this element to detect when an error occurred.
 *
 * Usage:
 * - Frontend: Rendered by ErrorBoundary fallback component
 * - Backend: Checked by UnfurlService.saveScreenshot() to detect errors
 */
export const ERROR_BOUNDARY_ID = 'lightdash-error-boundary';
/**
 * ID of the element rendered on the login page.
 * The UnfurlService checks for this element to detect when authentication failed
 * and the user was redirected to the login page instead of the requested content.
 *
 * Usage:
 * - Frontend: Rendered by LoginLanding Card component
 * - Backend: Checked by UnfurlService.saveScreenshot() to detect auth redirects
 */
export const LOGIN_PAGE_ID = 'lightdash-login-page';
/**
 * CSS Selectors (with prefix for direct use in querySelector/CSS)
 */
export const SCREENSHOT_SELECTORS = {
    /** ID selector: #lightdash-ready-indicator */
    READY_INDICATOR: `#${SCREENSHOT_READY_INDICATOR_ID}`,
    /** ID selector: #lightdash-screenshot-progress */
    PROGRESS_INDICATOR: `#${SCREENSHOT_PROGRESS_INDICATOR_ID}`,
    /** Class selector: .loading_chart_overlay */
    LOADING_OVERLAY: `.${LOADING_CHART_OVERLAY_CLASS}`,
    /** Class selector: .loading_chart */
    LOADING_CHART: `.${LOADING_CHART_CLASS}`,
    /** Class selector: .markdown-tile */
    MARKDOWN_TILE: `.${MARKDOWN_TILE_CLASS}`,
    /** Class selector: .react-grid-layout */
    DASHBOARD_GRID: `.${DASHBOARD_GRID_CLASS}`,
    /** Attribute selector: [data-custom-chart-ready-fallback] */
    CUSTOM_CHART_READY_FALLBACK: `[${CUSTOM_CHART_READY_FALLBACK_ATTRIBUTE}]`,
    /** ID selector: #lightdash-error-boundary */
    ERROR_BOUNDARY: `#${ERROR_BOUNDARY_ID}`,
    /** ID selector: #lightdash-login-page */
    LOGIN_PAGE: `#${LOGIN_PAGE_ID}`,
};
//# sourceMappingURL=screenshot.js.map